import { useEffect, useMemo, useRef } from 'react'

import { useSelector } from 'react-redux'

import type { MenuResponse, StandardRecipeItem } from '@library/api-menu-experience'
import type { RecipeUUID } from '@library/menu-service'

import { getBasketDate, getBasketRecipes, getNumPortions } from 'selectors/basket'
import { getBasketRecipeNumPortions, getBasketRecipeQuantity } from 'utils/basket'

import type { MenuQueryParams } from '../../../context/menuQueryContext'
import { getMenuExperienceMenuSWRKey } from './useMenuExperienceNavigation'
import { useMenuExperienceRecipeTileRefresh } from './useMenuExperienceRecipeTileRefresh'

type MenuExperienceBasketRecipeTileSyncQuery = MenuQueryParams &
  Record<string, unknown> & { collection?: string }

type SelectRecipeGridOption = (
  originalRecipeId: RecipeUUID,
  recipeId: RecipeUUID,
  numPortions: number,
) => Promise<void>

const getStandardRecipeItems = (
  contentBlock: MenuResponse['content'][number],
): StandardRecipeItem[] => {
  switch (contentBlock.type) {
    case 'recipe-grid':
      return contentBlock.items
    case 'campaign-banner':
      return contentBlock.items.filter((item) => item.type === 'standard-recipe')
    case 'compact-campaign-banner':
      return contentBlock.items
    default:
      return []
  }
}

const getOriginalRecipeIdForOption = (
  menuContent: MenuResponse['content'] | undefined,
  recipeId: RecipeUUID,
) => {
  for (const contentBlock of menuContent ?? []) {
    const recipe = getStandardRecipeItems(contentBlock).find(
      (item) => item.id !== recipeId && item.optionIds?.includes(recipeId),
    )

    if (recipe) {
      return recipe.id
    }
  }

  return undefined
}

export const useMenuExperienceBasketRecipeTileSync = (
  query: MenuExperienceBasketRecipeTileSyncQuery,
  menuId: string,
  menuContent?: MenuResponse['content'],
  selectRecipeGridOption?: SelectRecipeGridOption,
) => {
  const deliveryDate = useSelector(getBasketDate)
  const basketRecipes = useSelector(getBasketRecipes)
  const basketNumPortions = useSelector(getNumPortions)
  const { refreshRecipeTile } = useMenuExperienceRecipeTileRefresh({ menuId, menuContent, query })
  const menuSWRKey = getMenuExperienceMenuSWRKey({
    deliveryDate,
    numPortions: basketNumPortions,
    query,
  })
  const menuSWRKeyId = JSON.stringify(menuSWRKey)
  const previousSnapshotRef = useRef(new Map<RecipeUUID, number>())
  const previousMenuIdRef = useRef(menuId)
  const previousMenuSWRKeyIdRef = useRef(menuSWRKeyId)

  const snapshot = useMemo(() => {
    const nextSnapshot = new Map<RecipeUUID, number>()

    basketRecipes.forEach((recipe, recipeId) => {
      const quantity = getBasketRecipeQuantity(recipe)

      if (quantity > 0) {
        nextSnapshot.set(recipeId, getBasketRecipeNumPortions(recipe, basketNumPortions))
      }
    })

    return nextSnapshot
  }, [basketNumPortions, basketRecipes])

  useEffect(() => {
    const hasMenuChanged = previousMenuIdRef.current !== menuId
    const hasMenuSWRKeyChanged = previousMenuSWRKeyIdRef.current !== menuSWRKeyId
    const shouldReconcileAll = hasMenuChanged || hasMenuSWRKeyChanged
    const previousSnapshot = previousSnapshotRef.current
    const snapshotToCompare = shouldReconcileAll ? new Map<RecipeUUID, number>() : previousSnapshot

    previousSnapshotRef.current = snapshot
    previousMenuIdRef.current = menuId
    previousMenuSWRKeyIdRef.current = menuSWRKeyId
    snapshot.forEach((numPortions, recipeId) => {
      const previousNumPortions = snapshotToCompare.get(recipeId)
      const originalRecipeId =
        previousNumPortions === undefined
          ? getOriginalRecipeIdForOption(menuContent, recipeId)
          : undefined
      const hasCustomPortionsBeenAdded =
        previousNumPortions === undefined && numPortions !== basketNumPortions
      const havePortionsChanged =
        previousNumPortions !== undefined && previousNumPortions !== numPortions

      if (originalRecipeId && selectRecipeGridOption) {
        void selectRecipeGridOption(originalRecipeId, recipeId, numPortions)
      } else if (hasCustomPortionsBeenAdded || havePortionsChanged) {
        void refreshRecipeTile(recipeId, numPortions)
      }
    })

    previousSnapshot.forEach((_numPortions, recipeId) => {
      if (!snapshot.has(recipeId)) {
        void refreshRecipeTile(recipeId, basketNumPortions)
      }
    })
  }, [
    basketNumPortions,
    menuContent,
    menuId,
    menuSWRKeyId,
    refreshRecipeTile,
    selectRecipeGridOption,
    snapshot,
  ])
}
